import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { ArrowUpRight } from "lucide-react";

/*
  PRESSE — Medienspiegel · Anker #presse
  Kein Bildmaterial. Design analog Abschnitt "Realisiert".
*/

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "", testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1, delay, ease }}
    className={className}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const QUOTES = {
  de: [
    {
      text: "\u201EDas ist besser, als alles zu verbarrikadieren.\u201C",
      source:
        "Kirstin Bauch, Bezirksbürgermeisterin Charlottenburg-Wilmersdorf, in: Berliner Morgenpost, 17. Juli 2026",
    },
    {
      text:
        "\u201EDie neuen \u201ABerlin-Cubes\u2018 verbinden Aufenthaltsqualität, Umweltbildung und zertifizierte Fahrzeugsperren zu einem neuartigen Ansatz.\u201C",
      source: "Björn Leffler, ENTWICKLUNGSSTADT, 3. August 2026",
    },
    {
      text:
        "\u201EWürfelförmige Sperrelemente mit Stahlkernen wurden mit Holz verkleidet, bepflanzt und teils durch Sitzgelegenheiten ergänzt.\u201C",
      source: "Cay Dobberke, Der Tagesspiegel, 30. Juli 2026",
    },
  ],
  en: [
    {
      text: "\u201CThat is better than barricading everything.\u201D",
      source:
        "Kirstin Bauch, Mayor of Charlottenburg-Wilmersdorf district, in: Berliner Morgenpost, 17 July 2026",
    },
    {
      text:
        "\u201CThe new \u2018Berlin-Cubes\u2019 combine quality of public space, environmental education and certified vehicle barriers into a new kind of approach.\u201D",
      source: "Björn Leffler, ENTWICKLUNGSSTADT, 3 August 2026",
    },
    {
      text:
        "\u201CCube-shaped barrier elements with steel cores were clad in timber, planted and in places fitted with seating.\u201D",
      source: "Cay Dobberke, Der Tagesspiegel, 30 July 2026",
    },
  ],
};

const ITEMS = [
  {
    key: "entwicklungsstadt-cubes",
    url: "https://www.entwicklungsstadt.de/berlin-cubes-in-der-kantstrasse-urban-gardening-trifft-terrorschutz/",
    de: {
      meta: "ENTWICKLUNGSSTADT · 3. August 2026 · Björn Leffler",
      title: "Berlin-Cubes in der Kantstraße: Urban Gardening trifft Terrorschutz",
      text:
        "Ausführliche Darstellung des Pilotaufbaus: Wie bepflanzte Module geprüften Zufahrtsschutz mit Aufenthaltsqualität und Umweltbildung verbinden — und was das für die Gestaltung öffentlicher Plätze bedeutet.",
      hint: "PLUS-Artikel",
    },
    en: {
      meta: "ENTWICKLUNGSSTADT · 3 August 2026 · Björn Leffler",
      title: "Berlin-Cubes on Kantstraße: urban gardening meets vehicle protection",
      text:
        "A detailed account of the pilot installation: how planted modules combine certified vehicle protection with quality of public space and environmental education — and what that means for the design of city squares.",
      hint: "Subscriber article",
    },
  },
  {
    key: "entwicklungsstadt-breitscheidplatz",
    url: "https://www.entwicklungsstadt.de/mehr-sicherheit-weniger-poller-der-breitscheidplatz-vor-dem-umbau/",
    de: {
      meta: "ENTWICKLUNGSSTADT · 30. Juli 2026",
      title: "Mehr Sicherheit, weniger Poller: Breitscheidplatz vor dem Umbau",
      text:
        "Drei Lösungswege für den Breitscheidplatz im Vergleich. Das System in der Kantstraße steht für den Ansatz, Sicherheitstechnik als Stadtmobiliar zu gestalten statt als Barriere.",
    },
    en: {
      meta: "ENTWICKLUNGSSTADT · 30 July 2026",
      title: "More safety, fewer bollards: Breitscheidplatz before its rebuild",
      text:
        "Three possible approaches for Breitscheidplatz, compared. The installation on Kantstraße stands for the idea of designing protection as street furniture rather than as a barrier.",
    },
  },
  {
    key: "radio3",
    url: "https://www.radiodrei.de/programm/schema/sendungen/radio3_am_morgen/archiv/20260730_0600/radio3_aktuell_0710.html",
    de: {
      meta: "radio3 (rbb) · 30. Juli 2026 · radio3 am Morgen",
      title:
        "Mehr Sicherheit in der Stadt: Berlin-Cubes als Beispiel moderner Sicherheitsmodule",
      text:
        "Live-Interview mit Martin Fichtelmann bei Massimo Maio über die Frage, wie sich Plätze dauerhaft sichern lassen, ohne dass sie aussehen wie eine Festung.",
      hint: "Hörfunk",
    },
    en: {
      meta: "radio3 (rbb) · 30 July 2026 · radio3 am Morgen",
      title: "Safer cities: Berlin-Cubes as an example of modern security modules",
      text:
        "Live interview with Martin Fichtelmann, hosted by Massimo Maio, on how public squares can be permanently protected without ending up looking like a fortress.",
      hint: "Radio",
    },
  },
  {
    key: "tagesspiegel",
    url: "https://www.tagesspiegel.de/berlin/bezirke/charlottenburg-wilmersdorf/schutz-vor-terroranschlagen-in-berlin-der-breitscheidplatz-soll-endlich-schoner-werden--nur-wie-15890099.html",
    de: {
      meta: "Der Tagesspiegel · 30. Juli 2026 · Cay Dobberke",
      title:
        "Zehn Jahre nach dem Terroranschlag: Berliner Breitscheidplatz braucht neue Barrikaden — nur welche?",
      text:
        "Bestandsaufnahme im zehnten Jahr nach dem Anschlag. Der Aufbau in der Kantstraße wird als Ideengeber für eine gestalterisch bessere Lösung beschrieben.",
      hint: "Tagesspiegel Plus",
    },
    en: {
      meta: "Der Tagesspiegel · 30 July 2026 · Cay Dobberke",
      title: "Ten years after the attack: Breitscheidplatz needs new barriers — but which ones?",
      text:
        "A stocktake in the tenth year after the attack. The Kantstraße installation is described as a source of ideas for a better-designed solution.",
      hint: "Subscriber article",
    },
  },
  {
    key: "rbb24",
    url: null,
    de: {
      meta: "rbb24 BERLIN · 28. Juli 2026 · Fernsehbeitrag",
      title: "rbb24 BERLIN berichtet aus der Kantstraße",
      text: "Fernsehbeitrag über den Pilotaufbau in der Berliner Kantstraße.",
      hint: "Fernsehen",
    },
    en: {
      meta: "rbb24 BERLIN · 28 July 2026 · Television report",
      title: "rbb24 BERLIN reports from Kantstraße",
      text: "Television report on the pilot installation on Kantstraße in Berlin.",
      hint: "Television",
    },
  },
  {
    key: "morgenpost",
    url: null,
    de: {
      meta: "Berliner Morgenpost · 17. Juli 2026 · Norman Börner",
      title: "\u201ENicht alles verbarrikadieren\u201C",
      text:
        "Bericht von der Veranstaltung der AG City am Breitscheidplatz. Bezirksbürgermeisterin Kirstin Bauch, AG-City-Vorstand Patrick Wittke und Martin Fichtelmann werben gemeinsam für einen stadtverträglichen Schutz.",
      hint: "Print, Seite 15",
    },
    en: {
      meta: "Berliner Morgenpost · 17 July 2026 · Norman Börner",
      title: "\u201CNot barricading everything\u201D",
      text:
        "A report from the AG City event at Breitscheidplatz. District Mayor Kirstin Bauch, AG City board member Patrick Wittke and Martin Fichtelmann make a joint case for protection that suits the city.",
      hint: "Print, page 15",
    },
  },
  {
    key: "havelland",
    url: "https://unserhavelland.de/2019/04/25/martin-fichtelmann-erfindet-den-protection-cube-anti-terror-baeume/",
    de: {
      meta: "Unser Havelland · 25. April 2019 · Carsten Scheibe",
      title: "Martin Fichtelmann erfindet den Protection-Cube",
      text:
        "Das erste ausführliche Porträt: die Entstehung des Grundprinzips, die Patentierung 2018, der DEKRA-Test und der Ersteinsatz am Berliner Alexanderplatz.",
    },
    en: {
      meta: "Unser Havelland · 25 April 2019 · Carsten Scheibe",
      title: "Martin Fichtelmann invents the Protection-Cube",
      text:
        "The first full portrait: how the principle came about, the 2018 patent, the DEKRA crash test and the first installation at Berlin's Alexanderplatz.",
    },
  },
];

export default function PresseMedien() {
  const { lang } = useLang();
  const copy = {
    de: {
      kicker: "Presse",
      title: "Berlin-Cube in den Medien",
      intro:
        "Seit dem Pilotaufbau in der Kantstraße im Sommer 2026 berichten Tagespresse, Rundfunk und Fachmedien über das System. Eine Auswahl.",
      footPrefix: "Presseanfragen an ",
      footMail: "info@berlin-cube.de",
      footSuffix:
        " · Bildmaterial in Druckauflösung und ein Factsheet stellen wir auf Anfrage kurzfristig zur Verfügung.",
    },
    en: {
      kicker: "Press",
      title: "Berlin-Cube in the media",
      intro:
        "Since the pilot installation went up in the Kantstraße in the summer of 2026, the daily press, radio, television and trade media have reported on the system. A selection.",
      footPrefix: "Press enquiries to ",
      footMail: "info@berlin-cube.de",
      footSuffix:
        " · High-resolution images and a factsheet are available on request at short notice.",
    },
  }[lang];

  const quotes = QUOTES[lang];

  return (
    <section
      id="presse"
      data-testid="section-presse-medien"
      className="relative bg-[#0F1114] text-white py-32 md:py-48 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        {/* Kopf */}
        <div className="mb-16 md:mb-24 max-w-4xl">
          <Reveal>
            <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8 font-display">
              <span className="h-px w-8 bg-white/40" />
              {copy.kicker}
            </div>
            <h2
              className="font-display font-light tracking-[-0.03em] leading-[0.98] text-white"
              style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)" }}
              data-testid="presse-medien-title"
            >
              {copy.title}
            </h2>
            <p
              className="mt-8 md:mt-10 text-white/70 font-display font-light tracking-[-0.005em] leading-[1.4] max-w-3xl"
              style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.45rem)" }}
              data-testid="presse-medien-intro"
            >
              {copy.intro}
            </p>
          </Reveal>
        </div>

        {/* Zitatband */}
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-20 md:mb-28">
          {quotes.map((q, i) => (
            <Reveal
              key={i}
              delay={i * 0.08}
              className="bg-[#0F1114] p-8 md:p-10 flex flex-col justify-between gap-8"
              testid={`presse-quote-${i + 1}`}
            >
              <blockquote
                className="font-display font-light tracking-[-0.015em] leading-[1.3] text-white/90"
                style={{ fontSize: "clamp(1.15rem, 1.6vw, 1.5rem)" }}
              >
                {q.text}
              </blockquote>
              <div className="text-[11px] leading-relaxed tracking-[0.08em] uppercase text-white/40 font-display">
                {q.source}
              </div>
            </Reveal>
          ))}
        </div>

        {/* Liste der Beiträge */}
        <div className="border-t border-white/10">
          {ITEMS.map((item, i) => {
            const c = item[lang];
            return (
              <Reveal
                key={item.key}
                className="grid md:grid-cols-12 gap-4 md:gap-10 py-10 md:py-12 border-b border-white/10"
                testid={`presse-item-${item.key}`}
              >
                <div className="md:col-span-4 lg:col-span-3 text-[11px] tracking-[0.14em] uppercase text-white/45 font-display leading-relaxed">
                  {c.meta}
                </div>
                <div className="md:col-span-8 lg:col-span-9">
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`presse-link-${item.key}`}
                      className="group inline-flex items-start gap-3 text-white hover:text-[#F5D77A] transition-colors"
                    >
                      <h3
                        className="font-display font-light tracking-[-0.02em] leading-[1.15]"
                        style={{ fontSize: "clamp(1.3rem, 2vw, 1.85rem)" }}
                      >
                        {c.title}
                      </h3>
                      <ArrowUpRight
                        className="h-4 w-4 mt-2 shrink-0 opacity-50 transition-all group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        strokeWidth={1.5}
                      />
                    </a>
                  ) : (
                    <h3
                      className="font-display font-light tracking-[-0.02em] leading-[1.15] text-white"
                      style={{ fontSize: "clamp(1.3rem, 2vw, 1.85rem)" }}
                    >
                      {c.title}
                    </h3>
                  )}
                  <p className="mt-4 text-[14px] leading-relaxed text-white/60 max-w-3xl">
                    {c.text}
                  </p>
                  {c.hint && (
                    <span className="mt-4 inline-flex items-center text-[10px] tracking-[0.28em] uppercase text-white/40 border border-white/15 px-2.5 py-1 font-display">
                      {c.hint}
                    </span>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Abschlusszeile */}
        <Reveal testid="presse-medien-footnote">
          <p className="mt-14 text-[12px] text-white/40 italic max-w-2xl leading-relaxed border-l-2 border-white/20 pl-5">
            {copy.footPrefix}
            <a
              href="mailto:info@berlin-cube.de"
              data-testid="presse-medien-mail"
              className="not-italic text-white/70 hover:text-[#F5D77A] underline underline-offset-4 decoration-white/30 hover:decoration-[#F5D77A] transition-colors"
            >
              {copy.footMail}
            </a>
            {copy.footSuffix}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
